/**
 * OIDC Authorization Code + PKCE helpers for product login.
 */
function b64url(buf) {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : buf;
  let str = '';
  bytes.forEach((b) => {
    str += String.fromCharCode(b);
  });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(len = 64) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return b64url(arr);
}

async function sha256(plain) {
  const data = new TextEncoder().encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return b64url(digest);
}

export function getAuthMode() {
  const m = (process.env.REACT_APP_AUTH_MODE || 'dual').toLowerCase();
  if (m === 'oidc' || m === 'lab' || m === 'dual') return m;
  return 'dual';
}

// A baked loopback issuer (127.0.0.1) is a different origin from localhost or a LAN host.
// connect-src 'self' only allows the page origin, so lab token calls must use that origin.
function issuerForPage(configured) {
  const value = (configured || '').replace(/\/$/, '');
  if (!value) return `${window.location.origin}/realms/scada`;
  try {
    const url = new URL(value);
    const loopback = url.hostname === '127.0.0.1' || url.hostname === 'localhost' || url.hostname === '[::1]';
    if (loopback) return `${window.location.origin}${url.pathname}`.replace(/\/$/, '');
  } catch {
    /* keep the configured issuer */
  }
  return value;
}

export function getOidcConfig() {
  return {
    issuer: issuerForPage(process.env.REACT_APP_OIDC_ISSUER || 'http://127.0.0.1:8080/realms/scada'),
    clientId: process.env.REACT_APP_OIDC_CLIENT_ID || 'scada-web',
    redirectUri:
      process.env.REACT_APP_OIDC_REDIRECT_URI ||
      `${window.location.origin}${process.env.PUBLIC_URL || ''}/auth/callback`,
  };
}

export async function beginOidcLogin() {
  const cfg = getOidcConfig();
  const verifier = randomString(64);
  const challenge = await sha256(verifier);
  const state = randomString(24);
  const nonce = randomString(24);
  sessionStorage.setItem('oidc_pkce', JSON.stringify({ verifier, state, nonce, at: Date.now() }));
  const url = new URL(`${cfg.issuer}/protocol/openid-connect/auth`);
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('redirect_uri', cfg.redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid profile email');
  url.searchParams.set('state', state);
  url.searchParams.set('nonce', nonce);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export async function finishOidcLogin(searchParams) {
  const cfg = getOidcConfig();
  const raw = sessionStorage.getItem('oidc_pkce');
  if (!raw) throw new Error('Missing PKCE session');
  const pkce = JSON.parse(raw);
  sessionStorage.removeItem('oidc_pkce');
  if (searchParams.get('state') !== pkce.state) {
    throw new Error('Invalid OIDC state');
  }
  const code = searchParams.get('code');
  if (!code) throw new Error('Missing authorization code');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: cfg.clientId,
    code,
    redirect_uri: cfg.redirectUri,
    code_verifier: pkce.verifier,
  });
  const tokenRes = await fetch(`${cfg.issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!tokenRes.ok) {
    const t = await tokenRes.text();
    throw new Error(`Token exchange failed: ${t}`);
  }
  const tokens = await tokenRes.json();
  if (!tokens.access_token) throw new Error('No access_token');
  return tokens;
}

/** Resource Owner Password Credentials against the IdP (oidc-lab / Keycloak DAG). */
export async function passwordLogin(username, password) {
  const cfg = getOidcConfig();
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: cfg.clientId,
    username: String(username || '').trim(),
    password: String(password || ''),
  });
  const tokenRes = await fetch(`${cfg.issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!tokenRes.ok) {
    const t = await tokenRes.text();
    const err = new Error(tokenRes.status === 401 ? 'Invalid username or password' : `Token request failed: ${t}`);
    err.response = { status: tokenRes.status, data: t };
    throw err;
  }
  const tokens = await tokenRes.json();
  if (!tokens.access_token) throw new Error('No access_token');
  return tokens;
}
